import apiService from '@sonifi/wb-core/services/api.service';
import {
  getIdFromUrl,
  parseQueryParams,
} from '@sonifi/wb-core/utils/parseHelper';
import { ENDPOINTS } from '@sonifi/wb-core/services/endpointsConstants';
import {
  setRevenueCenters,
  setIrdSettings,
  setIrdSettingsLoading,
  setIrdSettingsLoadingSubmit,
  updateIrdSettings,
  updateRevenueCenter,
  setRevenueCenter,
} from '../../store/settings/irdSettingsSlice';
import { LOADING_STATUS } from '@sonifi/wb-core/common/Constants';
import {
  getProductsSettings,
  getSettingByProduct,
} from '@sonifi/wb-products/services/products.service';
import { PRODUCTS_KEYS } from '@sonifi/wb-products/constants/wbProductsConstants';
import { ORDERING_MODES_NAMES } from '../../constants/wbInRoomDiningConstants';
import { setAlert } from '@sonifi/wb-notifier/store/notifierSlice';
import uniqueId from 'lodash/uniqueId';

export const getRevenueCenters = (groupId) => async (dispatch) => {
  const revenueCentersUrl = `${ENDPOINTS.REVENUE_CENTERS}?${parseQueryParams({
    group_id: groupId,
  })}`;
  const revenueCenters = (await apiService.get(revenueCentersUrl)).data.objects;
  await dispatch(setRevenueCenters({ revenueCenters }));

  return revenueCenters;
};

export const checkRevenueCenter =
  ({ groupId, revenueCenterSelected }) =>
  async (dispatch) => {
    // Refresh revenue center if it's not the same group or not set
    const needRefresh =
      !revenueCenterSelected ||
      (revenueCenterSelected &&
        getIdFromUrl({
          url: revenueCenterSelected.group,
        }) !== groupId);

    if (needRefresh) {
      const revenueCenters = await dispatch(getRevenueCenters(groupId));
      revenueCenterSelected = revenueCenters[0];
      dispatch(setRevenueCenter({ revenueCenter: revenueCenterSelected }));
    }

    return revenueCenterSelected;
  };

const getImagesDependencies = async (irdSettings) => {
  const defaultItemImageUri = irdSettings.default_item_image.length
    ? irdSettings.default_item_image[0].image
    : null;
  let resItemImage = null;
  if (defaultItemImageUri) {
    const defaultItemImageId = getIdFromUrl({ url: defaultItemImageUri });
    resItemImage = await apiService.get(
      `${ENDPOINTS.IMAGES}/${defaultItemImageId}`,
    ).catch((error) => {
      console.error(error);
      return null;
    });
  }

  const defaultItemImage = resItemImage
    ? [
        {
          ...irdSettings.default_item_image[0],
          file_uri:
            resItemImage.data._embedded[resItemImage.data.files[0]].file_uri,
        },
      ]
    : [];

  const defaultItemCategoryUri = irdSettings.default_category_image.length
    ? irdSettings.default_category_image[0].image
    : null;
  let resCategoryImage = null;
  if (defaultItemCategoryUri) {
    const defaultCategoryImageId = getIdFromUrl({
      url: defaultItemCategoryUri,
    });
    resCategoryImage = await apiService.get(
      `${ENDPOINTS.IMAGES}/${defaultCategoryImageId}`,
    ).catch((error) => {
      console.error(error);
      return null;
    });
  }
  const defaultCategoryImage = resCategoryImage
    ? [
        {
          ...irdSettings.default_category_image[0],
          file_uri:
            resCategoryImage.data._embedded[resCategoryImage.data.files[0]]
              .file_uri,
        },
      ]
    : [];

  return {
    defaultItemImage,
    defaultCategoryImage,
  };
};

export const initIrdSettings =
  ({ groupId, platform }) =>
  async (dispatch, getState) => {
    dispatch(setIrdSettingsLoading(LOADING_STATUS.loading));
    try {
      const {
        groups: { selectedGroup },
        irdSettings: { revenueCenterSelected },
      } = getState();
      const irdSettingsParams = {
        group: selectedGroup.uri,
      };
      const res = await apiService.get(
        `${ENDPOINTS.IRD_SETTINGS}?${parseQueryParams(irdSettingsParams)}`,
      );

      const irdSettings = res.data.objects.find(
        (settings) => settings.revenue_center === revenueCenterSelected.uri,
      );

      await dispatch(getProductsSettings(groupId));
      const productSettings = dispatch(
        getSettingByProduct({
          groupId,
          productKey: PRODUCTS_KEYS.IRD,
        }),
      );

      const printerIp = irdSettings.printer_ip?.split('.') || [
        null,
        null,
        null,
        null,
      ];

      const IpFields = ['ipOne', 'ipTwo', 'ipThree', 'ipFour'];
      IpFields.forEach((value, index) => {
        irdSettings[value] = printerIp[index];
      });

      const orderingModes = [];

      Object.keys(productSettings.available_ordering_modes)
        .filter((key) => productSettings.available_ordering_modes[key])
        .forEach((name) => {
          orderingModes.push({
            display_name: ORDERING_MODES_NAMES[name],
            name,
          });
        });

      if (!Object.keys(irdSettings.questions).length) {
        irdSettings.questions = [
          {
            active: false,
            question: 'Please select from the following options:',
            answers: [],
          },
        ];
      } else {
        irdSettings.questions = irdSettings.questions.map((question) => ({
          ...question,
          answers: question.answers.map((answer, i) => ({
            id: uniqueId(`${Date.now()}_${i}`),
            title: answer,
          })),
        }));
      }

      if (
        !productSettings.available_ordering_modes[irdSettings.ordering_mode] &&
        orderingModes.length
      ) {
        irdSettings.adminChanges = {
          hasChanges: true,
          from: {
            display_name: ORDERING_MODES_NAMES[irdSettings.ordering_mode],
            name: irdSettings.ordering_mode,
          },
          to: orderingModes[0],
        };
      } else {
        irdSettings.adminChanges = {
          hasChanges: false,
          from: null,
          to: null,
        };
      }

      irdSettings.orderingModes = orderingModes;

      const imagesDependencies = await getImagesDependencies(irdSettings);

      await dispatch(
        setIrdSettings({
          irdSettings,
          productSettings,
          imagesDependencies,
          platform,
        }),
      );
      dispatch(setIrdSettingsLoading(LOADING_STATUS.success));
    } catch (error) {
      console.error(error);
      dispatch(setIrdSettingsLoading(LOADING_STATUS.error));
    }
  };

export const submitSettingsForm = (data) => async (dispatch, getState) => {
  dispatch(setIrdSettingsLoadingSubmit(LOADING_STATUS.loading));
  try {
    const {
      irdSettings: { revenueCenterSelected },
    } = getState();

    const printerIp =
      data.ordering_mode === 'on_site'
        ? `${data.ipOne}.${data.ipTwo}.${data.ipThree}.${data.ipFour}`
        : null;

    data.printer_ip = printerIp;

    data.ordering_mode = data.adminChanges.hasChanges
      ? data.adminChanges.to.name
      : data.ordering_mode;
    delete data.adminChanges;
    delete data.emailString;
    delete data.orderingModes;
    delete data.version;
    delete data.ipOne;
    delete data.ipTwo;
    delete data.ipThree;
    delete data.ipFour;
    data.questions = data.questions.map((question) => ({
      ...question,
      answers: question.answers.map((answer) => answer.title),
    }));

    const revenueCenter = revenueCenterSelected;
    const revenueCentersBody = {
      group: revenueCenter.group,
      id: revenueCenter.id,
      revenue_center_type: revenueCenter.revenue_center_type,
      title: data.display_name,
      uri: revenueCenter.uri,
    };

    const irdSettingsUrl = `${ENDPOINTS.IRD_SETTINGS}/${data.id}`;
    const revenueCentersUrl = `${ENDPOINTS.REVENUE_CENTERS}/${revenueCenter.id}`;

    const res = await apiService.patch(revenueCentersUrl, revenueCentersBody);
    await apiService.patch(irdSettingsUrl, data);

    await Promise.all([
      dispatch(updateRevenueCenter({ revenueCenter: res.data })),
      dispatch(
        setAlert({
          title: 'Settings Saved',
          content: 'These settings have been successfully saved.',
          type: 'success',
          show: true,
          dismissable: true,
        }),
      ),
      dispatch(setIrdSettingsLoadingSubmit(LOADING_STATUS.success)),
    ]);
  } catch (error) {
    console.error(error);
    dispatch(setIrdSettingsLoadingSubmit(LOADING_STATUS.error));
    dispatch(
      setAlert({
        title: 'Settings Save Failed',
        content: 'These settings could not be saved.',
        type: 'danger',
        show: true,
        dismissable: true,
      }),
    );
  }
};

export const saveImageThumbnail =
  ({ image, category = false }) =>
  async (dispatch, getState) => {
    try {
      const {
        irdSettings: { irdSettings },
      } = getState();

      const body = {
        image_url: image.file_uri,
        position: {
          width: image.regionWidth,
          height: image.regionHeight,
          anchor: [image.x, image.y],
        },
        scale: image.transform.scale,
        format: 'jpg',
        flip: image.transform.flip,
      };
      // Only Save when has file_uri
      const resImg = image.file_uri
        ? await apiService.post(ENDPOINTS.THUMBNAILS, body)
        : {
            data: {
              signed_url: '',
            },
          };

      const defaultImage = {
        display_uri: resImg.data.signed_url,
        file_uri: image.file_uri,
        image: image.image,
        transform: image.transform,
        x: image.x,
        y: image.y,
      };
      const newDefaultImage = defaultImage.file_uri ? [defaultImage] : [];
      const newIrdSettings = {
        ...irdSettings,
        [category ? 'default_category_image' : 'default_item_image']:
          newDefaultImage,
      };
      dispatch(updateIrdSettings({ irdSettings: newIrdSettings }));
      return newDefaultImage;
    } catch (error) {
      console.error(error);
      dispatch(
        setAlert({
          content:
            'The screen was unable to be updated. Please verify or edit the image.',
          type: 'danger',
          show: true,
          dismissable: true,
        }),
      );
    }
  };
